import * as React from "react";
import { Popover as PopoverPrimitive } from "radix-ui";

import { composeRippleProps, useTouchRipple } from "@/components/touch-ripple";
import { cn } from "@/lib/utils";

import { CircularProgress } from "./circular-progress";
import { MUI_MENU_ITEM } from "./mui-field-shape";
import { MuiPaper } from "./mui-paper";
import { MuiDeleteSmallIcon } from "./mui-spec-icons";
import {
  SPEC_ITEM_STATUS_DOT,
  SPEC_ITEM_STATUSES,
  type SpecItemStatus,
} from "@/components/ui/spec-item-status";

// A group's ⋮ menu — the donor's `MoreGroupMenu` (`components/specSheets/
// MoreGroupMenu.tsx`), shared in the source beside `SpecSheetNotes` (ported as
// `truncated-notes.tsx`), so shared here (#805).
//
// **Not a MUI `Menu`, so not a modal.** The donor draws a `Popper` —
// `placement='bottom-end'`, `minWidth: 200`, `zIndex: 9999`, portalled — round
// a `ClickAwayListener` round a `Paper` (`py: 1`, elevation 1, which the donor
// theme turns into a flat `0 0 0 1px #e0e0e0` ring) holding a `MenuList`
// (`py: 1` of its own, so the first row sits 16px down) with no `autoFocus`.
// Measured on the running donor: 36px rows, 1px dividers 8px above and below,
// 254.6px wide for the two-status list — the paper is as wide as its longest
// row. So:
//
//   - the page stays live underneath: nothing blocks it, and the focus stays
//     on the trigger when it opens;
//   - **a click, a touch-end or a focus outside closes it** — the three events
//     `react-click-away-listener` 2.4.0 listens for — but none in the tick it
//     opens in, none on its trigger (`GroupActionsMenu.tsx:30-36`), and none
//     whose target has already left the page by the time it reaches the
//     document (the listener's `!document.contains(target)`) — a focusless
//     control that swaps itself out as it is clicked. (A note's More / Less
//     is a native button: its press moves the focus, and the `focusin` has
//     closed the menu before the click, in the donor as here);
//   - **Escape does not**: nothing in the donor listens for it;
//   - **the keys are `MenuList`'s** (MUI 9.2, `MenuList.js`,
//     `useRovingTabIndex.js`): the arrows walk the rows and wrap, Home and End
//     jump to the ends, a printable key jumps to the next row starting with
//     what has been typed in the last 500ms, and the row last focused is the
//     list's one tab stop. Tab is not held: it leaves the menu as it would
//     any list, and the focus landing outside closes it.
//
// The rows, in order (`MoreGroupMenu.tsx:48-80`): `Edit Group Name/Note`; an
// optional reset row (below); a divider; one `Mark group as …` per status in `LineStatusText` order —
// finalized, in progress, needs attention — **less the group's current one**,
// each after a 16px round dot in the status colour, 8px before the words; a
// divider; the small `Delete` icon, 8px before `Delete`. Nothing is ever
// disabled. The current status is the group's **derived** one (#548 D4 /
// R3a): the donor's broken Ninja status hid `needs attention` on every group,
// a ruled parity break.
//
// **The optional reset row** is Atlas's own (#1084; a named scope addition,
// #1060 Q3, Q5–Q6): `Reset group name and notes to Base` (or `…to Core`),
// directly under `Edit Group Name/Note`, which writes the same two fields.
// It behaves as the line ⋮'s resets do: a click writes at once and leaves the
// menu open; while the write is in the air the row is disabled, its 20px
// spinner after the label (`ActionMenu.tsx:188-218`), and the keys skip it.
// Left unset, the menu draws exactly as the donor's.

export type GroupMenuStatus = SpecItemStatus;

/** The optional reset row: its label, its write in the air, its click. */
export interface GroupMenuReset {
  label: string;
  pending: boolean;
  onReset: () => void;
}

const STATUSES = SPEC_ITEM_STATUSES;
const STATUS_DOT = SPEC_ITEM_STATUS_DOT;

export interface MoreGroupMenuProps {
  open: boolean;
  /** A click, touch or focus outside the menu and its trigger. */
  onClose: () => void;
  /** The trigger the menu hangs from; its `id` names the menu. */
  anchor: React.ReactElement<{ id?: string }>;
  /** The group's current status, which the menu does not offer. */
  status: GroupMenuStatus;
  onEdit: () => void;
  onStatus: (status: GroupMenuStatus) => void;
  onDelete: () => void;
  /** The reset row, drawn only when given (#1084). */
  reset?: GroupMenuReset;
  /** The menu's own id — what the trigger's `aria-controls` points at. */
  id?: string;
}

/** The three events `react-click-away-listener` closes on. */
const CLICK_AWAY_EVENTS = ["click", "touchend", "focusin"] as const;

export function MoreGroupMenu({
  open,
  onClose,
  anchor,
  status,
  onEdit,
  onStatus,
  onDelete,
  reset,
  id,
}: MoreGroupMenuProps) {
  const content = React.useRef<HTMLDivElement>(null);
  // Radix types the anchor's ref as a `div`'s; the trigger is a button.
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const close = React.useEffectEvent(() => onClose());

  React.useEffect(() => {
    if (!open) return;
    const away = (event: Event) => {
      const target = event.target as Node | null;
      // Already gone from the page: a control that swapped itself out.
      if (!target || !document.contains(target)) return;
      if (content.current?.contains(target) || anchorRef.current?.contains(target)) return;
      close();
    };
    // Armed a tick late, as the listener is: the click that opened the menu is
    // still on its way up when the menu mounts.
    const arm = window.setTimeout(() => {
      for (const name of CLICK_AWAY_EVENTS) document.addEventListener(name, away);
    });
    return () => {
      window.clearTimeout(arm);
      for (const name of CLICK_AWAY_EVENTS) document.removeEventListener(name, away);
    };
  }, [open]);

  const rows = STATUSES.filter((other) => other !== status);
  // The rows before the statuses: Edit, and the reset row when offered.
  const lead = reset ? 2 : 1;
  const [tabStop, setTabStop] = React.useState(0);
  // MUI's `Popper` unmounts its `MenuList` on close, so each opening starts
  // its tab stop on the first row again.
  if (!open && tabStop !== 0) setTabStop(0);
  const typed = React.useRef<TypedKeys>({
    keys: [],
    repeating: true,
    previousKeyMatched: true,
    lastTime: 0,
  });

  /** `MenuList`'s key handling, over the rows as they stand. */
  const onListKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    // Radix's focus scope loops Tab inside the content; `MenuList` holds no
    // Tab at all, so it is kept from reaching the scope and goes its own way.
    if (event.key === "Tab") {
      event.stopPropagation();
      return;
    }
    // A disabled row is out of the walk, as `MenuList` skips one.
    const all = [
      ...event.currentTarget.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ),
    ];
    const at = all.indexOf(document.activeElement as HTMLElement);
    // The list itself holds the focus after a click on its padding; from
    // there, down is the first row and up the last (`useRovingTabIndex.js`).
    const onList = at === -1;
    const focusAt = (index: number) => all[index]?.focus();
    if (!(event.altKey || event.shiftKey || event.ctrlKey || event.metaKey)) {
      const to =
        event.key === "ArrowDown"
          ? onList
            ? 0
            : (at + 1) % all.length
          : event.key === "ArrowUp"
            ? onList
              ? all.length - 1
              : (at - 1 + all.length) % all.length
            : event.key === "Home"
              ? 0
              : event.key === "End"
                ? all.length - 1
                : undefined;
      if (to !== undefined) {
        event.preventDefault();
        focusAt(to);
        return;
      }
    }
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
    const criteria = typed.current;
    const key = event.key.toLowerCase();
    const now = performance.now();
    if (criteria.keys.length > 0) {
      if (now - criteria.lastTime > TYPEAHEAD_RESET_MS) {
        criteria.keys = [];
        criteria.repeating = true;
        criteria.previousKeyMatched = true;
      } else if (criteria.repeating && key !== criteria.keys[0]) {
        criteria.repeating = false;
      }
    }
    criteria.lastTime = now;
    criteria.keys.push(key);
    const current = all[at];
    const keepCurrent = current && !criteria.repeating && rowMatches(current, criteria);
    // From the list, every row is a candidate; from a row, every other one.
    const from = onList ? 0 : at + 1;
    const next = all.findIndex(
      (_, offset) =>
        (onList || offset < all.length - 1) &&
        rowMatches(all[(from + offset) % all.length]!, criteria),
    );
    const found = next === -1 ? undefined : (from + next) % all.length;
    if (criteria.previousKeyMatched && (keepCurrent || found !== undefined)) {
      event.preventDefault();
      if (!keepCurrent && found !== undefined) focusAt(found);
    } else {
      criteria.previousKeyMatched = false;
    }
  };

  /** The tab stop follows the focus (`useRovingTabIndex.js`'s `onFocus`). */
  const onListFocus = (event: React.FocusEvent<HTMLUListElement>) => {
    const all = [...event.currentTarget.querySelectorAll('[role="menuitem"]')];
    const index = all.indexOf(event.target);
    if (index !== -1) setTabStop(index);
  };

  return (
    <PopoverPrimitive.Root open={open}>
      <PopoverPrimitive.Anchor asChild ref={anchorRef}>
        {anchor}
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={content}
          id={id}
          // A `Popper`, not a dialog: no role of its own, named by its trigger.
          role={undefined}
          aria-labelledby={anchor.props.id}
          side="bottom"
          align="end"
          sideOffset={0}
          // The donor's dismissals are the click-away above; Radix's own —
          // Escape, a press or a focus outside — are all turned off, and the
          // focus is left where it was on both ends.
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className="z-[9999] min-w-[200px] outline-none"
        >
          <MuiPaper elevation={1} className="rounded-[4px] py-2">
            <ul
              role="menu"
              // `MenuList`'s own `tabIndex: -1`: a click on its padding focuses
              // the list, and the keys still answer from there.
              tabIndex={-1}
              className="m-0 list-none py-2"
              onKeyDown={onListKeyDown}
              onFocus={onListFocus}
            >
              <MenuRow tabStop={tabStop === 0} onSelect={onEdit}>
                Edit Group Name/Note
              </MenuRow>
              {reset ? (
                <MenuRow
                  tabStop={tabStop === 1}
                  disabled={reset.pending}
                  onSelect={reset.onReset}
                  data-testid="group-menu-reset"
                >
                  {reset.label}
                  {reset.pending ? (
                    // `CircularProgress size={20} sx={{ ml: 2 }}`, as the line ⋮'s.
                    <CircularProgress size={20} className="ml-4" />
                  ) : null}
                </MenuRow>
              ) : null}
              <MenuDivider />
              {rows.map((row, index) => (
                <MenuRow
                  key={row}
                  tabStop={tabStop === index + lead}
                  onSelect={() => onStatus(row)}
                >
                  <span
                    data-slot="group-status-dot"
                    className={cn("mr-2 size-4 shrink-0 rounded-[50%]", STATUS_DOT[row])}
                  />
                  <span>Mark group as {row.replace("_", " ")}</span>
                </MenuRow>
              ))}
              <MenuDivider />
              <MenuRow tabStop={tabStop === rows.length + lead} onSelect={onDelete}>
                <MuiDeleteSmallIcon className="mr-2" />
                Delete
              </MenuRow>
            </ul>
          </MuiPaper>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** `MenuList`'s typeahead state (`MenuList.js`'s `textCriteriaRef`). */
interface TypedKeys {
  keys: string[];
  repeating: boolean;
  previousKeyMatched: boolean;
  lastTime: number;
}

const TYPEAHEAD_RESET_MS = 500;

/** `textCriteriaMatches`: the row's words, trimmed and lower-cased, start with
 *  what was typed — or, while one key repeats, with that key. */
function rowMatches(row: HTMLElement, criteria: TypedKeys) {
  const text = (row.innerText ?? row.textContent ?? "").trim().toLowerCase();
  if (text.length === 0) return false;
  return criteria.repeating
    ? text[0] === criteria.keys[0]
    : text.startsWith(criteria.keys.join(""));
}

/**
 * MUI's `MenuItem` — a `ButtonBase` `li`: Enter picks it on the way down, Space
 * on the way up, a press ripples (no focus ripple: `MenuItem` passes none).
 * 48px tall below `sm` and as tall as its line above it (`MenuItem.js:71,141`).
 */
function MenuRow({
  tabStop,
  disabled = false,
  onSelect,
  children,
  "data-testid": testId,
}: {
  tabStop: boolean;
  /** MUI's disabled `MenuItem`: 0.38 opacity, no pointer, no focus. */
  disabled?: boolean;
  onSelect: () => void;
  children: React.ReactNode;
  "data-testid"?: string;
}) {
  const { rippleTargetProps, rippleLayer } = useTouchRipple({ disabled });
  return (
    <li
      role="menuitem"
      data-testid={testId}
      aria-disabled={disabled ? true : undefined}
      tabIndex={tabStop && !disabled ? 0 : -1}
      className={cn(
        MUI_MENU_ITEM,
        "cursor-pointer whitespace-nowrap select-none max-sm:min-h-12",
        disabled && "pointer-events-none opacity-[0.38]",
      )}
      onClick={disabled ? undefined : onSelect}
      {...composeRippleProps<HTMLLIElement>(rippleTargetProps, {
        onKeyDown: (event) => {
          if (disabled) return;
          if (event.key === "Enter") {
            event.preventDefault();
            onSelect();
          } else if (event.key === " ") event.preventDefault();
        },
        onKeyUp: (event) => {
          if (event.key === " " && !disabled) onSelect();
        },
      })}
    >
      {children}
      {rippleLayer}
    </li>
  );
}

/** `<Divider component='li' />` inside a `MenuList`: 1px of `divider`, 8px
 *  above and below (`MenuItem.js`'s `& + .MuiDivider-root`). */
function MenuDivider() {
  return (
    <li role="separator" className="border-lennar-divider my-2 border-0 border-b border-solid" />
  );
}
